import React, { memo, useState } from 'react';
import { Film } from 'lucide-react';
import { AgentService } from '@/services/agent';
import { useAgentStore } from '@/stores/agentStore';
import { SearchResultsList, buildSearchListProps } from '../SearchResultCard';
import { WorkflowPreview } from '../WorkflowPreview';
import { MediaResultCard } from '../MediaResultCard';
import { GenerationConfirmPanel } from '../GenerationParamsPanel';
import { renderFormattedMessage, TypewriterTextBlock, ProcessLogPanel } from './ProcessLogPanel';
import { AIReplyBlock } from './AIReplyBlock';
import type { Message } from '../types';

interface UserMessageBubbleProps {
  message: Message;
}

export const UserMessageBubble: React.FC<UserMessageBubbleProps> = memo(({ message: m }) => {
  // P2-6: 图片加载失败追踪（按索引记录失败状态）
  const [failedRefImgs, setFailedRefImgs] = useState<Set<number>>(new Set());
  const [failedAttImgs, setFailedAttImgs] = useState<Set<number>>(new Set());
  const handleRefImgError = (idx: number) => {
    setFailedRefImgs(prev => { const n = new Set(prev); n.add(idx); return n; });
  };
  const handleAttImgError = (idx: number) => {
    setFailedAttImgs(prev => { const n = new Set(prev); n.add(idx); return n; });
  };

  return (
    <div className="flex flex-col max-w-[90%] gap-1.5 items-end">
      {((m.references && m.references.length > 0) || (m.attachments && m.attachments.length > 0)) && (
        <div className="flex items-center gap-1 justify-end">
          {m.references?.map((ref, idx) => (
            <div
              key={ref.id}
              className="shrink-0 rounded-md overflow-hidden border border-white/10"
              style={{ width: 36, height: 36 }}
            >
              {ref.thumbnailUrl && !failedRefImgs.has(idx) ? (
                <img
                  src={ref.thumbnailUrl}
                  className="w-full h-full object-cover"
                  draggable={false}
                  alt={ref.title || '参考'}
                  onError={() => handleRefImgError(idx)}
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <span className="text-[9px] text-slate-500">{(ref.title || '参考').slice(0, 2)}</span>
                </div>
              )}
            </div>
          ))}
          {m.attachments?.map((att, idx) => (
            <div
              key={`att-${idx}`}
              className="shrink-0 rounded-md overflow-hidden border border-white/10"
              style={{ width: 36, height: 36 }}
            >
              {att.thumbnailUrl && !failedAttImgs.has(idx) ? (
                att.file?.type?.startsWith('video/') ? (
                  <div className="relative w-full h-full">
                    <video src={att.thumbnailUrl} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Film size={10} className="text-white/80" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={att.thumbnailUrl}
                    className="w-full h-full object-cover"
                    draggable={false}
                    alt={att.file?.name || '附件'}
                    onError={() => handleAttImgError(idx)}
                  />
                )
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <span className="text-[9px] text-slate-500">{att.file?.name?.split('.').pop()?.slice(0, 3) || att.name?.split('.').pop()?.slice(0, 3) || '?'}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="group relative transition-all w-full">
        <div className="relative px-4 py-2.5 rounded-2xl select-text cursor-text bg-[#2a2a2c] text-slate-100">
          <p className="leading-6 text-[12px] whitespace-pre-wrap">{m.text}</p>
        </div>
      </div>
    </div>
  );
});

// 🔴 v5-#12: 对对象类型使用 JSON.stringify 比较，避免引用不同导致不必要的重渲染
function shallowEqualObjects(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function areAssistantMessagesEqual(prev: AssistantMessageBubbleProps, next: AssistantMessageBubbleProps) {
  return (
    prev.message.id === next.message.id &&
    prev.message.text === next.message.text &&
    prev.message.type === next.message.type &&
    shallowEqualObjects(prev.message.searchData, next.message.searchData) &&
    shallowEqualObjects(prev.message.mediaData, next.message.mediaData) &&
    shallowEqualObjects(prev.message.generationOptions, next.message.generationOptions)
  );
}

interface AssistantMessageBubbleProps {
  message: Message;
  isHistory?: boolean;
  onGenerationConfirm: (params: any, msg: Message) => void;
  onGenerationCancel: (msg: Message) => void;
  onApplyWorkflow: (wf: any) => void;
}

export const AssistantMessageBubble: React.FC<AssistantMessageBubbleProps> = memo(({
  message: m,
  isHistory = false,
  onGenerationConfirm,
  onGenerationCancel,
  onApplyWorkflow,
}) => {
  const sessionId = useAgentStore((s) => s.sessionId);
  // 🔵 P0-5: 搜索消息判断条件统一为严格判断（与 MessageList.tsx 保持一致）
  // 只有明确标记为 type==='search' 且携带 searchData 的消息才渲染为搜索结果
  // 这避免了"type='text' 但 searchData 意外保留"的边缘情况被误判为搜索消息
  const shouldShowSearchResults = m.type === 'search' && m.searchData;
  // 🔴 v5-#3: 搜索消息恢复失败时 searchData 为 null，显示专用提示
  const shouldShowSearchUnavailable = m.type === 'search' && !m.searchData;

  return (
    <AIReplyBlock
      content={
        <div className="space-y-3">
          {m.introText && (
            <div className="text-[12px] leading-6 text-gray-300 select-text cursor-text">
              {renderFormattedMessage(m.introText, false)}
            </div>
          )}
          {m.processLogs && m.processLogs.length > 0 && (
            <ProcessLogPanel logs={m.processLogs} title="处理记录" />
          )}
          {/* 搜索结果显示 */}
          {shouldShowSearchResults ? (
            <SearchResultsList {...buildSearchListProps(m.searchData, undefined)} />
          ) : shouldShowSearchUnavailable ? (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5">
              <p className="text-[12px] text-amber-300">⚠️ 搜索结果数据不可用，请重新搜索</p>
            </div>
          ) : m.type === 'generation_confirm' ? (
            m.generationOptions ? (
              <GenerationConfirmPanel
                options={m.generationOptions}
                onConfirm={(params) => onGenerationConfirm(params, m)}
                onCancel={() => onGenerationCancel(m)}
              />
            ) : (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5">
                <p className="text-[12px] text-amber-300">⚠️ 生图确认数据加载异常，请重新发送消息</p>
              </div>
            )
          ) : m.type === 'workflow' && m.workflowData ? (
            <WorkflowPreview
              workflow={m.workflowData}
              onApply={(wf) => onApplyWorkflow(wf)}
            />
          ) : (m.type === 'image' || m.type === 'video') && m.mediaData ? (
            <div className="space-y-3">
              {m.text && (
                <div className="text-[12px] leading-6 text-gray-300 select-text cursor-text whitespace-pre-wrap">
                  {m.text}
                </div>
              )}
              <MediaResultCard data={m.mediaData} />
            </div>
          ) : (
            <TypewriterTextBlock
              text={m.text}
              speed={12}
              className="text-[12px] leading-6 text-gray-300 select-text cursor-text"
              skipAnimation={isHistory}
            />
          )}
        </div>
      }
      showActions={true}
      onCopy={() => navigator.clipboard.writeText(m.text)}
      onRate={(type) => {
        if (m.id && sessionId) {
          AgentService.submitFeedback(m.id, sessionId, type === 'up' ? 5 : 1);
        }
      }}
    />
  );
}, areAssistantMessagesEqual);
